<template>
  <LoadingDiv v-if="!isReady" />

  <template v-else>
    <Teleport to="body">
      <div class="principal-modal-backdrop" :data-ui-template="uiTemplate.id" v-if="currPrincipal" @click.self="handlePrincipalReset">
        <div class="principal-modal sba-slide-in" role="dialog" aria-modal="true" aria-label="Individual principal report" @keydown.esc.stop="handlePrincipalReset">
          <EnrolleeDetailInsight
            v-if="isDirectInsight(currPrincipal)"
            :enrolleeId="currPrincipal.enrollee_id || currPrincipal.id"
            @reset="handlePrincipalReset"
          />
          <PrincipalDetailInsight
            v-else
            :clientId="clientId"
            :principalId="currPrincipal.id"
            :policyId="selectedPolicy"
            @reset="handlePrincipalReset"
          />
        </div>
      </div>
    </Teleport>

    <div class="client-detail-stack sba-slide-in">
      <div class="card-panel red lighten-5 red-text text-darken-3" v-if="error">{{ error }}</div>

      <div class="card-panel client-overview-card z-depth-0">
        <div class="client-overview-head">
          <div class="client-title-block">
            <span class="client-status-icon" :class="{ danger: currClient.utilization_rate >= 50 }">
              <i class="material-icons">{{ currClient.utilization_rate < 50 ? "check_circle" : "flag" }}</i>
            </span>
            <div>
              <label class="active teal-text">Client Insight</label>
              <h5>{{ currClient.name }}</h5>
              <p>
              <span class="chip grey lighten-4">{{ currClient.cat?.toUpperCase() || "" }}</span>
              <span class="chip teal lighten-5" v-if="currClient.policy">{{ currClient.policy.year }}</span>
                <small class="grey-text" v-if="currClient.policy">{{ policyDateRange(currClient.policy) }}</small>
              </p>
            </div>
          </div>
          <button class="btn-flat close-detail-button" title="Close" @click.prevent="handleBack">
            <i class="material-icons left">close</i>Close
          </button>
        </div>

        <div class="client-overview-body">
            <div class="overview-controls">
              <div>
                <div class="input-field" v-if="currClient.policies.length">
                  <select v-model="selectedPolicy" @change="handlePolicyChange">
                    <option :value="policy.id" v-for="policy in currClient.policies" :key="policy.id">
                      {{ policy.year }} | {{ policyDateRange(policy) }}
                    </option>
                  </select>
                  <label>Policy Year</label>
                </div>
              </div>
              <div class="right-align">
                <button class="btn-small waves-effect waves-light cyan darken-3" @click.prevent="downloadClient">
                  <i class="material-icons left">download</i>Download
                </button>
              </div>
            </div>

            <div class="overview-metrics">
                <div class="overview-metric total">
                <h6>{{ formatMoney(currClient.invoice.generated) }}</h6>
                <small>Total Invoices</small>
                </div>
                <div class="overview-metric paid">
                <h6>{{ formatMoney(currClient.invoice.paid) }}</h6>
                <small>Premium Received</small>
                </div>
                <div class="overview-metric pending">
                <h6>{{ formatMoney(currClient.invoice.pending) }}</h6>
                <small>Pending Invoices</small>
                </div>
                <div class="overview-metric mlr">
                <h6 :class="mlrClass(currClient.gross_mlr ?? currClient.utilization_rate)">{{ currClient.gross_mlr ?? currClient.utilization_rate }}%</h6>
                <small>Gross MLR | <b>{{ formatMoney(currClient.encounter.gross_sent ?? currClient.encounter.sent) }}</b> billed</small>
                <small class="real-mlr-line">Real MLR {{ currClient.real_mlr ?? 0 }}% | {{ formatMoney(currClient.encounter.real_approved) }} vetted</small>
                </div>
            </div>

            <div class="progress">
              <div
                class="determinate"
                :class="barClass(currClient.utilization_rate)"
                :style="`width: ${barWidth(currClient.utilization_rate)}%`"
              ></div>
            </div>
            <div class="invoice-stack" :title="`Premium received ${formatMoney(currClient.invoice.paid)} | Pending invoices ${formatMoney(currClient.invoice.pending)}`">
              <span class="paid" :style="`width: ${invoiceWidth(currClient.invoice.paid, currClient.invoice.generated)}%`"></span>
              <span class="pending" :style="`width: ${invoiceWidth(currClient.invoice.pending, currClient.invoice.generated)}%`"></span>
            </div>
            <small class="grey-text">
              <span class="legend-chip"><i class="legend-box paid"></i>Premium Received</span>
              <span class="legend-chip"><i class="legend-box pending"></i>Pending Invoices</span>
            </small>
        </div>
      </div>

      <div class="card-panel">
        <h6>Monthly Utilization</h6>
        <div class="month-row" v-for="month in currClient.monthly_utilization" :key="month.month">
          <span>{{ month.month }}</span>
          <div class="month-bar">
            <span :style="`width: ${monthlyWidth(month.sent)}%`"></span>
          </div>
          <strong>{{ formatMoney(month.sent) }}</strong>
        </div>
        <p class="grey-text" v-if="!currClient.monthly_utilization.length">No encounters for this policy.</p>
      </div>

      <div class="card-panel">
        <h6>Invoice Periods</h6>
        <div class="period-row" v-for="period in currClient.invoice_periods" :key="period.id">
          <div>
            <strong>{{ formatDate(period.start) }} - {{ formatDate(period.end) }}</strong><br />
            <small>
              {{ period.status.toUpperCase() }} |
              {{ formatMoney(period.invoice.generated) }} total invoice |
              {{ formatMoney(period.invoice.paid) }} premium received |
              {{ period.encounter.count }} encounters
            </small>
          </div>
          <div class="right-align">
            <strong :class="periodMlrClass(period)">{{ periodMlrLabel(period) }}</strong><br />
            <small>{{ formatMoney(period.encounter.gross_sent ?? period.encounter.sent) }} gross utilization</small><br />
            <small>{{ formatMoney(period.encounter.real_approved) }} real utilization</small>
          </div>
          <div class="progress period-progress">
            <div
              class="determinate"
              :class="barClass(period.utilization_rate)"
              :style="`width: ${barWidth(period.utilization_rate)}%`"
            ></div>
          </div>
          <div class="invoice-stack period-stack">
            <span class="paid" :style="`width: ${invoiceWidth(period.invoice.paid, period.invoice.generated)}%`"></span>
            <span class="pending" :style="`width: ${invoiceWidth(period.invoice.pending, period.invoice.generated)}%`"></span>
          </div>
        </div>
        <div class="period-total-row">
          <div>
            <strong>Policy total</strong><br />
            <small>{{ formatMoney(currClient.invoice.paid) }} premium received | {{ formatMoney(currClient.invoice.pending) }} pending invoices</small>
          </div>
          <div class="right-align">
            <strong :class="mlrClass(currClient.gross_mlr ?? currClient.utilization_rate)">{{ currClient.gross_mlr ?? currClient.utilization_rate }}% Gross MLR</strong><br />
            <small>{{ currClient.real_mlr ?? 0 }}% Real MLR | {{ formatMoney(currClient.encounter.real_approved) }} vetted</small>
          </div>
        </div>
        <p class="grey-text period-note">
          Policy MLR uses total utilization divided by premium received. Invoice-period MLR is not additive.
        </p>
      </div>

      <div class="card-panel">
        <h6>Principals / Enrollees</h6>
        <div class="row">
          <div class="col s12 m5">
            <div class="input-field principal-search">
              <input
                type="search"
                v-model="principalSearch"
                placeholder="Search principal, enrollee ID or plan"
                @keyup.enter="handlePrincipalSearch"
                @search="handlePrincipalSearch"
              />
            </div>
          </div>
          <div class="col s12 m7">
            <span
              class="band-chip"
              :class="{ active: principalBand === band.value }"
              v-for="band in principalBands"
              :key="band.value"
              @click="handlePrincipalBand(band.value)"
            >
              {{ band.label }}
            </span>
          </div>
        </div>
        <div class="collection">
          <a
            href="#"
            class="collection-item principal-row"
            v-for="principal in pagedPrincipals"
            :key="principal.row_key || principal.id"
            @click.prevent="handlePrincipal(principal)"
          >
            <div>
              <strong>{{ principal.name }}</strong><br />
              <small v-if="isDirectInsight(principal)">
                Direct enrollee | {{ principal.enrollee_number || "No ID" }} | {{ principal.plan?.name || "No plan" }}
              </small>
              <small v-else>{{ principal.enrollee_number || "No ID" }} | {{ principal.plan?.name || "No plan" }}</small>
            </div>
            <div class="right-align">
              <strong class="mlr-muted" v-if="isDirectInsight(principal)">Utilization only</strong>
              <strong :class="mlrClass(principal.gross_mlr ?? principal.utilization_rate)" v-else>{{ principal.gross_mlr ?? principal.utilization_rate }}% Gross MLR</strong><br />
              <small>{{ formatMoney(principal.encounter.gross_sent ?? principal.encounter.sent) }} gross | {{ formatMoney(principal.encounter.real_approved) }} real</small>
            </div>
          </a>
        </div>
        <div class="principal-pager" v-if="principalPageTotal > 1">
          <button
            class="btn-flat principal-page-button"
            :class="{ disabled: principalPage <= 1 }"
            @click.prevent="changePrincipalPage(principalPage - 1)"
          >
            <i class="material-icons left">chevron_left</i>Previous
          </button>
          <span>Page {{ principalPage }} of {{ principalPageTotal }}</span>
          <button
            class="btn-flat principal-page-button"
            :class="{ disabled: principalPage >= principalPageTotal }"
            @click.prevent="changePrincipalPage(principalPage + 1)"
          >
            Next<i class="material-icons right">chevron_right</i>
          </button>
        </div>
        <p class="grey-text" v-if="!filteredPrincipals.length">No principals match this filter.</p>
      </div>
    </div>
  </template>
</template>

<script>
import uiTemplate from "@/ui/active-template";
import { onMounted, onUpdated, ref, computed } from "vue";
import getItems from "@/composables/getItems";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PrincipalDetailInsight from "@/components/insight/PrincipalDetailInsightComponent.vue";
import EnrolleeDetailInsight from "@/components/insight/EnrolleeDetailInsightComponent.vue";
import { downloadExcelFx } from "@/composables/reportFunction/downloader";
import nativeUi from "@/ui/native-ui";

export default {
  name: "clientsInsightComponent",
  components: {
    LoadingDiv,
    PrincipalDetailInsight,
    EnrolleeDetailInsight,
  },
  props: ["clientId", "initialPolicyId"],
  emits: ["reset"],
  setup(props, context) {
    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const selectedPolicy = ref(null);
    const currPrincipal = ref(null);
    const principalSearch = ref("");
    const principalBand = ref("all");
    const principalPage = ref(1);
    const principalPageSize = 10;
    const principalBands = ref([
      { value: "all", label: "All" },
      { value: "low", label: "Low < 50%" },
      { value: "watch", label: "Watch 50-74%" },
      { value: "high", label: "High 75-99%" },
      { value: "over", label: "Over 100%" },
    ]);

    const initSelect = () => {
      const elemsSelect = document.querySelectorAll("select");
      nativeUi.FormSelect.init(elemsSelect, {});
    };

    onMounted(initSelect);
    onUpdated(initSelect);

    const formatMoney = (val) => {
      return `NGN ${Number(val || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    };

    const formatDate = (val) => {
      if (!val) return "";
      return new Date(val).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const policyDateRange = (policy) => {
      if (!policy?.start_date || !policy?.end_date) return "";
      return `${formatDate(policy.start_date)} - ${formatDate(policy.end_date)}`;
    };

    const barWidth = (val) => Math.min(Number(val || 0), 100);

    const barClass = (val) => {
      if (val >= 100) return "red darken-1";
      if (val > 70) return "orange darken-1";
      return "green darken-1";
    };

    const mlrClass = (val) => {
      if (val >= 100) return "mlr-red";
      if (val > 70) return "mlr-orange";
      return "mlr-green";
    };

    const periodMlrLabel = (period) => {
      if (Number(period?.invoice?.paid || 0) <= 0 && Number(period?.encounter?.sent || 0) > 0) {
        return "N/A Gross MLR";
      }

      return `${period?.gross_mlr ?? period?.utilization_rate ?? 0}% Gross MLR`;
    };

    const periodMlrClass = (period) => {
      if (Number(period?.invoice?.paid || 0) <= 0 && Number(period?.encounter?.sent || 0) > 0) {
        return "mlr-muted";
      }

      return mlrClass(period?.gross_mlr ?? period?.utilization_rate ?? 0);
    };

    const invoiceWidth = (val, total) => {
      if (!Number(total || 0)) {
        return 0;
      }

      return Math.min(Math.round((Number(val || 0) / Number(total || 0)) * 100), 100);
    };

    const monthlyWidth = (sent) => {
      const max = Math.max(...currClient.value.monthly_utilization.map((x) => Number(x.sent || 0)), 0);
      if (!max) return 0;
      return Math.round((Number(sent || 0) / max) * 100);
    };

    const buildClientInsightUrl = (policyId = null, page = 1) => {
      const params = [];

      if (policyId) {
        params.push(`policy_id=${policyId}`);
      }

      params.push(`principal_page=${page}`);
      params.push(`principal_per_page=${principalPageSize}`);

      if (principalSearch.value) {
        params.push(`principal_q=${encodeURIComponent(principalSearch.value)}`);
      }

      if (principalBand.value && principalBand.value !== "all") {
        params.push(`principal_band=${principalBand.value}`);
      }

      return `operator/insight/utilization/client/${props.clientId}?${params.join("&")}`;
    };

    const getItemsFromApi = (policyId = null, page = principalPage.value) => {
      (async () => {
        isReady.value = false;
        const url = buildClientInsightUrl(policyId, page);
        const { res, err } = await getItems(url);

        if (res.value) {
          response.value = res.value.data;
          selectedPolicy.value = res.value.data.policy?.id || null;
          principalPage.value = res.value.data.principals?.current_page || 1;
        } else {
          error.value = err.value;
        }

        isReady.value = true;
      })();
    };

    onMounted(() => {
      getItemsFromApi(props.initialPolicyId);
    });

    const handlePolicyChange = () => {
      currPrincipal.value = null;
      principalPage.value = 1;
      getItemsFromApi(selectedPolicy.value, 1);
    };

    const handleBack = () => {
      context.emit("reset", true);
    };

    const handlePrincipal = (principal) => {
      currPrincipal.value = principal;
    };

    const handlePrincipalReset = () => {
      currPrincipal.value = null;
    };

    const isDirectInsight = (principal) => {
      return principal?.insight_type === "direct_enrollee" || Number(principal?.principal_id || 0) === 0 && principal?.enrollee_id;
    };

    const handlePrincipalSearch = () => {
      principalPage.value = 1;
      getItemsFromApi(selectedPolicy.value, 1);
    };

    const handlePrincipalBand = (band) => {
      principalBand.value = band;
      principalPage.value = 1;
      getItemsFromApi(selectedPolicy.value, 1);
    };

    const downloadClient = () => {
      const data = [
        {
          Section: "Summary",
          Name: currClient.value.name,
          Detail: currClient.value.policy?.year || "",
          "Policy Start Date": currClient.value.policy?.start_date || "",
          "Policy End Date": currClient.value.policy?.end_date || "",
          "Total Invoices": currClient.value.invoice.generated,
          "Premium Received": currClient.value.invoice.paid,
          "Pending Invoices": currClient.value.invoice.pending,
          Encounters: currClient.value.encounter.count,
          "Gross Utilization": currClient.value.encounter.gross_sent ?? currClient.value.encounter.sent,
          "Real Utilization": currClient.value.encounter.real_approved,
          "Gross MLR": `${currClient.value.gross_mlr ?? currClient.value.utilization_rate}%`,
          "Real MLR": `${currClient.value.real_mlr ?? 0}%`,
        },
        {},
      ];

      currClient.value.invoice_periods.forEach((period, index) => {
        data.push({
          Section: index === 0 ? "Invoice Periods" : "",
          Name: `${period.start} - ${period.end}`,
          Detail: period.status === "c" ? "Paid" : "",
          "Total Invoices": period.invoice.generated,
          "Premium Received": period.invoice.paid,
          "Pending Invoices": period.invoice.pending,
          Encounters: period.encounter.count,
          "Gross Utilization": period.encounter.gross_sent ?? period.encounter.sent,
          "Real Utilization": period.encounter.real_approved,
          "Gross MLR": `${period.gross_mlr ?? period.utilization_rate}%`,
          "Real MLR": `${period.real_mlr ?? 0}%`,
        });
      });

      data.push({});

      currClient.value.monthly_utilization.forEach((month, index) => {
        data.push({
          Section: index === 0 ? "Monthly Utilization" : "",
          Name: month.month,
          Detail: "",
          "Total Invoices": "",
          "Premium Received": "",
          "Pending Invoices": "",
          Encounters: month.encounter_count,
          "Gross Utilization": month.gross_sent ?? month.sent,
          "Real Utilization": month.real_approved,
          MLR: "",
        });
      });

      data.push({});

      currClient.value.provider_utilization.forEach((provider, index) => {
        data.push({
          Section: index === 0 ? "Provider Utilization" : "",
          Name: provider.provider?.name || "Provider",
          Detail: "",
          "Total Invoices": "",
          "Premium Received": "",
          "Pending Invoices": "",
          Encounters: provider.encounter_count,
          "Gross Utilization": provider.gross_sent ?? provider.sent,
          "Real Utilization": provider.real_approved,
          MLR: "",
        });
      });

      data.push({});

      filteredPrincipals.value.forEach((principal, index) => {
        data.push({
          Section: index === 0 ? "Principals / Families" : "",
          Name: principal.name,
          Detail: `${principal.enrollee_number || ""} ${principal.plan?.name || ""}`.trim(),
          "Policy Start Date": currClient.value.policy?.start_date || "",
          "Policy End Date": currClient.value.policy?.end_date || "",
          "Total Invoices": principal.invoice.generated,
          "Premium Received": principal.invoice.paid,
          "Pending Invoices": principal.invoice.pending,
          Encounters: principal.encounter.count,
          "Gross Utilization": principal.encounter.gross_sent ?? principal.encounter.sent,
          "Real Utilization": principal.encounter.real_approved,
          "Gross MLR": `${principal.gross_mlr ?? principal.utilization_rate}%`,
          "Real MLR": `${principal.real_mlr ?? 0}%`,
        });
      });

      if (data.length) {
        downloadExcelFx(data, `${currClient.value.name}_Utilization_${currClient.value.policy?.year || ""}`);
      }
    };

    const currClient = computed(() => response.value || {
      policies: [],
      invoice_periods: [],
      monthly_utilization: [],
      provider_utilization: [],
      principals: {
        data: [],
        current_page: 1,
        last_page: 1,
        per_page: principalPageSize,
        total: 0,
      },
      invoice: {},
      encounter: {
        sent: 0,
        gross_sent: 0,
        real_approved: 0,
      },
      utilization_rate: 0,
      gross_mlr: 0,
      real_mlr: 0,
    });

    const filteredPrincipals = computed(() => {
      return currClient.value.principals?.data || [];
    });

    const principalPageTotal = computed(() => {
      return currClient.value.principals?.last_page || 1;
    });

    const pagedPrincipals = computed(() => {
      return filteredPrincipals.value;
    });

    const changePrincipalPage = (page) => {
      if (page < 1 || page > principalPageTotal.value) return;
      principalPage.value = page;
      getItemsFromApi(selectedPolicy.value, page);
    };

    return {
      uiTemplate,
      handleBack,
      handlePolicyChange,
      handlePrincipal,
      handlePrincipalReset,
      isDirectInsight,
      handlePrincipalSearch,
      handlePrincipalBand,
      changePrincipalPage,
      downloadClient,
      formatMoney,
      formatDate,
      policyDateRange,
      barWidth,
      barClass,
      mlrClass,
      periodMlrLabel,
      periodMlrClass,
      invoiceWidth,
      monthlyWidth,
      isReady,
      error,
      selectedPolicy,
      currPrincipal,
      principalSearch,
      principalBand,
      principalBands,
      principalPage,
      principalPageTotal,
      currClient,
      filteredPrincipals,
      pagedPrincipals,
    };
  },
};
</script>

<style scoped>
.clickable {
  cursor: pointer;
}

.m-0 {
  margin: 0;
}

.client-detail-stack {
  display: grid;
  gap: 0;
  width: 100%;
}

.client-detail-stack > .card-panel {
  margin: 0;
  width: 100%;
}

.client-detail-stack > .card-panel + .card-panel {
  border-top: 0;
}

.mlr-green {
  color: #2e7d32;
}

.mlr-orange {
  color: #ef6c00;
}

.mlr-red {
  color: #c62828;
}

.mlr-muted {
  color: #78909c;
}

.principal-modal-backdrop {
  box-sizing: border-box;
  color: var(--ui-foreground);
  font-family: var(--ui-font-sans);
  font-size: .875rem;
  line-height: 1.5;
  align-items: flex-start;
  background: rgba(38, 50, 56, 0.52);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  overflow-y: auto;
  padding: 28px 14px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 3000;
}

.principal-modal {
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 18px 42px rgba(38, 50, 56, 0.28);
  max-width: 1120px;
  padding: 14px;
  position: relative;
  width: 100%;
  z-index: 3001;
}

.principal-modal :deep(*),
.principal-modal :deep(*::before),
.principal-modal :deep(*::after) {
  box-sizing: border-box;
}

@layer components {
  .principal-modal-backdrop .principal-modal {
    background: var(--ui-background) !important;
    border: 1px solid var(--ui-border) !important;
    border-radius: var(--ui-radius) !important;
    max-height: calc(100dvh - 56px) !important;
    max-width: 1120px !important;
    overflow: hidden !important;
    padding: 1rem !important;
    width: 100% !important;
  }
}

.principal-modal :deep(.principal-detail-shell),
.principal-modal :deep(.enrollee-detail-shell) {
  max-height: calc(100dvh - 90px);
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.principal-modal :deep(.card-panel) {
  border-color: var(--ui-border);
  box-shadow: none;
  min-width: 0;
  transform: none;
}

.principal-modal :deep(.card-panel:hover) {
  box-shadow: none;
  transform: none;
}

.principal-modal :deep(.principal-title-block h5) {
  font-size: 1.125rem;
  font-weight: 600;
}

.principal-modal :deep(.family-panel h6),
.principal-modal :deep(.monthly-panel h6) {
  font-size: 1rem;
  font-weight: 600;
}

.principal-modal :deep(.family-panel .collection) {
  border: 0;
  border-radius: 0;
  list-style: none;
  padding: 0;
}

.principal-modal :deep(.family-panel .collection-item) {
  border-bottom: 1px solid var(--ui-border);
  padding: .875rem 0;
}

.principal-modal :deep(.family-panel .collection-item strong) {
  font-size: .875rem;
  font-weight: 600;
}

.principal-modal :deep(.family-panel .collection-item small) {
  color: var(--ui-muted-foreground);
  font-size: .75rem;
}

@media (max-width: 600px) {
  .principal-modal-backdrop { padding: 12px; }
  @layer components {
    .principal-modal-backdrop .principal-modal { max-height: calc(100dvh - 24px) !important; padding: .5rem !important; }
  }
  .principal-modal :deep(.principal-detail-shell),
  .principal-modal :deep(.enrollee-detail-shell) { max-height: calc(100dvh - 44px); }
}

.card-panel,
.principal-modal {
  animation: insightCardIn 0.24s ease both;
}

.card-panel {
  border: 1px solid #e0e0e0;
  border-radius: 0;
  box-shadow: none;
  color: #263238;
  padding: 24px 28px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.client-detail-stack > .card-panel:hover {
  border-color: #e0e0e0;
  box-shadow: none;
  transform: none;
}

.client-detail-stack > .card-panel:first-child {
  border-radius: 7px 7px 0 0;
}

.client-detail-stack > .card-panel:last-child {
  border-radius: 0 0 7px 7px;
  box-shadow: 0 18px 36px -34px rgba(38, 50, 56, 0.7);
}

.client-overview-card {
  background: #fff;
  padding: 0;
}

.client-overview-head {
  align-items: center;
  border-bottom: 1px solid #e7e7e7;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  min-height: 126px;
  padding: 26px 28px;
}

.client-title-block {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  min-width: 0;
}

.client-status-icon {
  align-items: center;
  background: #e0f2f1;
  border-radius: 999px;
  color: #00838f;
  display: flex;
  flex-shrink: 0;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.client-status-icon.danger {
  background: #ffebee;
  color: #c62828;
}

.client-status-icon i {
  font-size: 24px;
}

.client-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
  overflow-wrap: anywhere;
}

.client-title-block p {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0 0;
}

.client-title-block .chip {
  margin: 0;
}

.client-title-block small {
  display: inline-block;
  margin-left: 2px;
}

.close-detail-button {
  color: #455a64;
  flex-shrink: 0;
  font-weight: 600;
  text-transform: none;
}

.client-overview-body {
  padding: 24px 28px;
}

.overview-controls {
  align-items: end;
  border-bottom: 1px solid #e7e7e7;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(220px, 360px) 1fr;
  margin-bottom: 18px;
  padding-bottom: 18px;
}

.overview-controls .input-field {
  margin-bottom: 0;
}

.overview-metrics {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 10px;
}

.overview-metric {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  min-height: 96px;
  padding: 16px;
}

.overview-metric h6 {
  color: #263238;
  font-size: 1.08rem;
  font-weight: 700;
  margin: 0 0 7px;
  overflow-wrap: anywhere;
}

.overview-metric small {
  color: #607d8b;
}

.overview-metric .real-mlr-line {
  display: block;
  font-size: 0.78rem;
  margin-top: 5px;
}

.overview-metric.total {
  background: #e3f2fd;
  border-color: #bbdefb;
}

.overview-metric.paid {
  background: #e0f2f1;
  border-color: #b2dfdb;
}

.overview-metric.pending {
  background: #fff8e1;
  border-color: #ffecb3;
}

.overview-metric.mlr {
  background: #ffebee;
  border-color: #ffcdd2;
}

.period-row {
  border-bottom: 1px solid #eeeeee;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  padding: 10px 0;
}

.period-total-row {
  background: #f7fafb;
  border: 1px solid #e3ebee;
  border-radius: 4px;
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr auto;
  margin-top: 12px;
  padding: 11px 12px;
}

.period-note {
  font-size: 0.86rem;
  margin: 8px 0 0;
}

.period-progress {
  grid-column: 1 / -1;
  margin: 0;
}

.month-row {
  align-items: center;
  display: grid;
  grid-template-columns: 72px 1fr auto;
  gap: 10px;
  margin: 10px 0;
}

.month-bar {
  background: #eceff1;
  height: 8px;
}

.month-bar span {
  background: #00838f;
  display: block;
  height: 8px;
}

.principal-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.principal-search {
  margin-top: 0;
}

.principal-pager {
  align-items: center;
  border-top: 1px solid #eeeeee;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 12px;
  padding-top: 14px;
}

.principal-pager span {
  color: #607d8b;
  font-size: 0.88rem;
  font-weight: 600;
}

.principal-page-button {
  color: #455a64;
  font-weight: 600;
  text-transform: none;
}

.band-chip {
  background: #eceff1;
  border-radius: 2px;
  cursor: pointer;
  display: inline-block;
  margin: 0 8px 10px 0;
  padding: 8px 12px;
}

.band-chip.active {
  background: #00838f;
  color: #ffffff;
}

.invoice-stack {
  background: #eceff1;
  display: flex;
  height: 8px;
  margin-top: 10px;
  width: 100%;
}

.period-stack {
  grid-column: 1 / -1;
  margin-top: 0;
}

.invoice-stack span {
  display: block;
  height: 8px;
}

.progress .determinate,
.month-bar span,
.invoice-stack span {
  animation: insightBarIn 0.38s ease both;
  transform-origin: left center;
  transition: width 0.28s ease, filter 0.18s ease;
}

.progress:hover .determinate,
.month-bar:hover span,
.invoice-stack:hover span {
  filter: saturate(1.15);
}

.invoice-stack .paid {
  background: #26a69a;
}

.invoice-stack .pending {
  background: #ffca28;
}

.legend-chip {
  display: inline-block;
  margin-right: 10px;
}

.legend-box {
  display: inline-block;
  height: 8px;
  margin-right: 5px;
  width: 16px;
}

.legend-box.paid {
  background: #26a69a;
}

.legend-box.pending {
  background: #ffca28;
}

@media only screen and (max-width: 992px) {
  .principal-pager {
    align-items: stretch;
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    justify-content: stretch;
  }

  .principal-pager span {
    grid-column: 1 / -1;
    order: -1;
  }

  .principal-page-button {
    align-items: center;
    border: 0;
    border-radius: 0;
    display: inline-flex;
    justify-content: center;
    min-height: 44px;
    padding: 0 12px;
    width: 100%;
  }

  .principal-page-button i.left,
  .principal-page-button i.right {
    float: none;
    margin: 0 6px 0 0;
  }

  .principal-page-button i.right {
    margin: 0 0 0 6px;
  }
}

@media only screen and (max-width: 600px) {
  .client-overview-head {
    align-items: flex-start;
    flex-direction: column;
    padding: 22px;
  }

  .client-overview-body {
    padding: 22px;
  }

  .overview-controls,
  .overview-metrics {
    grid-template-columns: 1fr;
  }

  .overview-metric {
    margin-bottom: 10px;
  }
}

@keyframes insightCardIn {
  from {
    opacity: 0.88;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes insightBarIn {
  from {
    opacity: 0.55;
    transform: scaleX(0.35);
  }
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}
</style>
